'use client'

import { LineInfoCircle } from '@qr-front/icons/line-info-circle'
import { LineLock } from '@qr-front/icons/line-lock'
import { cn } from '@qr-front/ui/utils'
import { QrPasswordFields } from 'features/shared/client'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { RESET } from 'jotai/utils'
import { useTranslations } from 'next-intl'

import { TGeneratorFormSection } from '../../components/GeneratorFormSection'
import {
	PASSWORD_DESIGN_SECTION_ID,
	passwordAtom,
	passwordEnabledAtom,
	passwordRepeatAtom,
	passwordSubmitAttemptedAtom,
} from './passwordDesignState'
import { useLoadSavedQrPassword } from './useLoadSavedQrPassword'

const PasswordDesignFormContent = () => {
	const t = useTranslations()
	const [isEnabled, setIsEnabled] = useAtom(passwordEnabledAtom)
	const [password, setPassword] = useAtom(passwordAtom)
	const [repeat, setRepeat] = useAtom(passwordRepeatAtom)
	const submitAttempted = useAtomValue(passwordSubmitAttemptedAtom)
	const setSubmitAttempted = useSetAtom(passwordSubmitAttemptedAtom)

	return (
		<div className="flex flex-col gap-6 tablet:gap-8">
			<div className="flex items-center gap-2">
				<button
					type="button"
					role="switch"
					aria-checked={isEnabled}
					aria-labelledby="qr-password-switch-label"
					onClick={() => {
						setIsEnabled(!isEnabled)
						setPassword(RESET)
						setRepeat(RESET)
						setSubmitAttempted(RESET)
					}}
					className={cn(
						'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors duration-200',
						isEnabled ? 'bg-blue' : 'bg-border-grey',
					)}
				>
					<span
						className={cn(
							'inline-block size-5 rounded-full bg-white shadow transition-transform duration-200',
							isEnabled
								? 'translate-x-[1.375rem]'
								: 'translate-x-0.5',
						)}
					/>
				</button>
				<span id="qr-password-switch-label" className="type-body-small">
					{t(
						'generator__design_form__password_section__switch_label',
					)}
				</span>
			</div>
			{isEnabled && (
				<div className="flex flex-col gap-4">
					<QrPasswordFields
						password={password}
						repeat={repeat}
						onPasswordChange={(value) => setPassword(value)}
						onRepeatChange={(value) => setRepeat(value)}
						submitAttempted={submitAttempted}
						layout="sideBySide"
					/>
					<div className="flex items-center gap-3 rounded-8 bg-purple-8 p-4">
						<LineInfoCircle className="size-6 shrink-0 text-blue" />
						<p className="type-body-small text-dark-grey">
							{t(
								'generator__design_form__password_section__note',
							)}
						</p>
					</div>
				</div>
			)}
		</div>
	)
}

export const usePasswordDesignForm = (): TGeneratorFormSection => {
	const t = useTranslations()

	useLoadSavedQrPassword()

	return {
		id: PASSWORD_DESIGN_SECTION_ID,
		title: t('generator__design_form__password_section__title'),
		description: t('generator__design_form__password_section__description'),
		icon: <LineLock />,
		Content: PasswordDesignFormContent,
	}
}
